import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { 
  Square, Circle, TableProperties, Armchair, Users, 
  Save, RotateCcw, ZoomIn, ZoomOut, Grid, Move,
  Plus, Minus, Trash2, Copy, Layers
} from 'lucide-react';
import { getBaseUrl, BRAND_NAME } from '@/config/appConfig';
import { getEmbedTargetOrigin } from '../utils/embedOrigin';

const API_URL = getBaseUrl();

// Embeddable Layout Designer - Lightweight version for embedding
export default function EmbedDesigner() {
  const [searchParams] = useSearchParams();
  const apiKey = searchParams.get('key');
  const mode = searchParams.get('mode') || 'embed';
  const organiserId = searchParams.get('organiserId') || '';
  const organiserName = searchParams.get('organiserName') || '';
  const eventId = searchParams.get('eventId') || '';
  const eventName = searchParams.get('eventName') || '';
  
  const canvasRef = useRef(null);
  const [branding, setBranding] = useState(null);
  const [layoutName, setLayoutName] = useState('New Layout');
  const [venueName, setVenueName] = useState('');
  const [canvasObjects, setCanvasObjects] = useState([]);
  const [selectedTool, setSelectedTool] = useState('select');
  const [selectedObject, setSelectedObject] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [showGrid, setShowGrid] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

  // Load branding
  useEffect(() => {
    const loadBranding = async () => {
      try {
        const response = await fetch(`${API_URL}/api/public/branding?key=${apiKey}`);
        if (response.ok) {
          const data = await response.json();
          setBranding(data.branding);
        }
      } catch (err) {
        console.warn('Could not load branding');
      }
    };
    if (apiKey) loadBranding();
  }, [apiKey]);

  // Post message to parent
  const postMessage = useCallback((type, data = {}) => {
    if (window.parent !== window) {
      window.parent.postMessage({ type: `seatpilot:${type}`, data }, getEmbedTargetOrigin());
    }
  }, []);

  // Notify parent when ready
  useEffect(() => {
    postMessage('ready', { mode });
  }, [postMessage, mode]);

  // Tool definitions
  const tools = [
    { id: 'select', icon: Move, label: 'Select' },
    { id: 'single_row', icon: Square, label: 'Single Row' },
    { id: 'multiple_rows', icon: Layers, label: 'Multi Row' },
    { id: 'round_table', icon: Circle, label: 'Round Table' },
    { id: 'rect_table', icon: TableProperties, label: 'Rect Table' },
    { id: 'seat', icon: Armchair, label: 'Single Seat' },
    { id: 'ga_zone', icon: Users, label: 'GA Zone' },
  ];

  // Add object to canvas
  const addObject = useCallback((type, x, y) => {
    const newObject = {
      id: `obj_${Date.now()}`,
      type,
      x,
      y,
      rotation: 0,
      properties: getDefaultProperties(type),
      seats: []
    };

    // Generate seats based on type
    if (type === 'single_row') {
      const seatCount = newObject.properties.seat_count || 10;
      for (let i = 1; i <= seatCount; i++) {
        newObject.seats.push({
          seat_number: i,
          bookable: true,
          x: (i - 1) * 35,
          y: 0
        });
      }
    } else if (type === 'round_table') {
      const tableSeats = newObject.properties.seats || 8;
      for (let i = 1; i <= tableSeats; i++) {
        const angle = (i - 1) * (360 / tableSeats);
        newObject.seats.push({
          seat_number: i,
          bookable: true,
          angle
        });
      }
    }

    setCanvasObjects(prev => [...prev, newObject]);
    setSelectedObject(newObject.id);
    postMessage('object_added', { object: newObject });
  }, [postMessage]);

  // Get default properties for object type
  const getDefaultProperties = (type) => {
    switch (type) {
      case 'single_row':
        return { row_label: 'A', seat_count: 10, spacing: 35, seat_size: 30 };
      case 'multiple_rows':
        return { start_label: 'A', row_count: 5, seat_count: 10, row_spacing: 40, seat_spacing: 35 };
      case 'round_table':
        return { label: 'T1', seats: 8, radius: 60 };
      case 'rect_table':
        return { label: 'T1', seats: 6, width: 120, height: 60 };
      case 'seat':
        return { label: 'S1' };
      case 'ga_zone':
        return { label: 'GA', capacity: 100, width: 200, height: 150 };
      default:
        return {};
    }
  };

  // Handle canvas click
  const handleCanvasClick = (e) => {
    if (selectedTool === 'select') return;
    
    const rect = canvasRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / zoom;
    const y = (e.clientY - rect.top) / zoom;
    
    addObject(selectedTool, x, y);
    setSelectedTool('select');
  };

  // Handle object drag start
  const handleDragStart = (e, objectId) => {
    if (selectedTool !== 'select') return;
    
    e.stopPropagation();
    setSelectedObject(objectId);
    setIsDragging(true);
    
    const rect = canvasRef.current.getBoundingClientRect();
    const obj = canvasObjects.find(o => o.id === objectId);
    
    setDragOffset({
      x: e.clientX - rect.left - obj.x * zoom,
      y: e.clientY - rect.top - obj.y * zoom
    });
  };

  // Handle mouse move for dragging
  const handleMouseMove = (e) => {
    if (!isDragging || !selectedObject) return;
    
    const rect = canvasRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left - dragOffset.x) / zoom;
    const y = (e.clientY - rect.top - dragOffset.y) / zoom;
    
    setCanvasObjects(prev => prev.map(obj =>
      obj.id === selectedObject ? { ...obj, x: Math.max(0, x), y: Math.max(0, y) } : obj
    ));
  };

  // Handle mouse up
  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Delete selected object
  const deleteSelected = () => {
    if (!selectedObject) return;
    setCanvasObjects(prev => prev.filter(obj => obj.id !== selectedObject));
    setSelectedObject(null);
  };

  // Update selected object property
  const updateObjectProperty = (key, value) => {
    if (!selectedObject) return;
    
    setCanvasObjects(prev => prev.map(obj => {
      if (obj.id !== selectedObject) return obj;
      
      const newObj = {
        ...obj,
        properties: { ...obj.properties, [key]: value }
      };
      
      // Regenerate seats if seat count changes
      if (key === 'seat_count' && obj.type === 'single_row') {
        newObj.seats = [];
        for (let i = 1; i <= value; i++) {
          newObj.seats.push({
            seat_number: i,
            bookable: true,
            x: (i - 1) * (obj.properties.spacing || 35),
            y: 0
          });
        }
      }
      
      return newObj;
    }));
  };

  // Save layout
  const saveLayout = async () => {
    if (!apiKey) {
      setError('API key required');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/api/external/layouts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': apiKey
        },
        body: JSON.stringify({
          name: layoutName,
          venue_name: venueName,
          organiser_id: organiserId,
          organiser_name: organiserName,
          event_id: eventId,
          event_name: eventName,
          canvas_objects: canvasObjects
        })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to save layout');
      }

      const data = await response.json();
      postMessage('layout_created', data);
      postMessage('layout_saved', data);
      
    } catch (err) {
      setError(err.message);
      postMessage('error', { message: err.message });
    } finally {
      setSaving(false);
    }
  };

  // Clear canvas
  const clearCanvas = () => {
    if (window.confirm('Clear all objects from canvas?')) {
      setCanvasObjects([]);
      setSelectedObject(null);
    }
  };

  // Get colors from branding
  const colors = branding?.colors || {};
  const primaryColor = colors.primary || '#6366f1';

  // Get selected object data
  const selectedObjData = canvasObjects.find(obj => obj.id === selectedObject);

  return (
    <div 
      className="h-screen flex flex-col overflow-hidden"
      style={{ 
        fontFamily: branding?.fonts?.primary || 'Inter, system-ui, sans-serif',
        backgroundColor: colors.background || '#ffffff'
      }}
    >
      {/* Toolbar */}
      <div 
        className="flex items-center gap-2 p-3 border-b"
        style={{ borderColor: colors.surface || '#e5e7eb' }}
      >
        {/* Tool buttons */}
        <div className="flex items-center gap-1 px-2 py-1 bg-gray-100 rounded-lg">
          {tools.map(tool => (
            <Button
              key={tool.id}
              variant={selectedTool === tool.id ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setSelectedTool(tool.id)}
              title={tool.label}
              style={selectedTool === tool.id ? { backgroundColor: primaryColor } : {}}
            >
              <tool.icon className="h-4 w-4" />
            </Button>
          ))}
        </div>

        <div className="h-6 w-px bg-gray-300" />

        {/* Zoom controls */}
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="sm" onClick={() => setZoom(z => Math.max(0.25, z - 0.25))}>
            <ZoomOut className="h-4 w-4" />
          </Button>
          <span className="text-sm w-12 text-center">{Math.round(zoom * 100)}%</span>
          <Button variant="ghost" size="sm" onClick={() => setZoom(z => Math.min(2, z + 0.25))}>
            <ZoomIn className="h-4 w-4" />
          </Button>
        </div>

        <Button 
          variant={showGrid ? 'default' : 'ghost'} 
          size="sm" 
          onClick={() => setShowGrid(!showGrid)}
          style={showGrid ? { backgroundColor: primaryColor } : {}}
        >
          <Grid className="h-4 w-4" />
        </Button>

        <div className="flex-1" />

        {/* Actions */}
        <Button variant="ghost" size="sm" onClick={clearCanvas}>
          <RotateCcw className="h-4 w-4 mr-1" />
          Clear
        </Button>
        
        {selectedObject && (
          <Button variant="ghost" size="sm" onClick={deleteSelected} className="text-red-600">
            <Trash2 className="h-4 w-4 mr-1" />
            Delete
          </Button>
        )}

        <Button 
          onClick={saveLayout} 
          disabled={saving || canvasObjects.length === 0}
          style={{ backgroundColor: primaryColor }}
        >
          <Save className="h-4 w-4 mr-2" />
          {saving ? 'Saving...' : 'Save Layout'}
        </Button>
      </div>

      <div className="flex-1 flex overflow-hidden">
        {/* Canvas */}
        <div 
          className="flex-1 overflow-auto p-4"
          style={{ backgroundColor: colors.surface || '#f9fafb' }}
        >
          {error && (
            <Alert className="mb-4" variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div
            ref={canvasRef}
            className="relative bg-white rounded-lg shadow-sm cursor-crosshair"
            style={{
              width: 800 * zoom,
              height: 600 * zoom,
              backgroundImage: showGrid ? 
                `linear-gradient(to right, #e5e7eb 1px, transparent 1px),
                 linear-gradient(to bottom, #e5e7eb 1px, transparent 1px)` : 'none',
              backgroundSize: `${20 * zoom}px ${20 * zoom}px`,
              cursor: selectedTool === 'select' ? 'default' : 'crosshair'
            }}
            onClick={handleCanvasClick}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
          >
            {/* Render objects */}
            {canvasObjects.map(obj => (
              <div
                key={obj.id}
                className={`absolute cursor-move ${selectedObject === obj.id ? 'ring-2 ring-blue-500' : ''}`}
                style={{
                  left: obj.x * zoom,
                  top: obj.y * zoom,
                  transform: `rotate(${obj.rotation}deg)`,
                  transformOrigin: 'top left'
                }}
                onMouseDown={(e) => handleDragStart(e, obj.id)}
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedObject(obj.id);
                }}
              >
                {renderObject(obj, zoom, colors, selectedObject === obj.id)}
              </div>
            ))}
          </div>
        </div>

        {/* Properties Panel */}
        <div className="w-64 border-l bg-white p-4 overflow-auto">
          <Card>
            <CardHeader className="py-3">
              <CardTitle className="text-sm">Layout Settings</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <Label className="text-xs">Layout Name</Label>
                <Input 
                  value={layoutName}
                  onChange={(e) => setLayoutName(e.target.value)}
                  className="h-8 text-sm"
                />
              </div>
              <div>
                <Label className="text-xs">Venue Name</Label>
                <Input 
                  value={venueName}
                  onChange={(e) => setVenueName(e.target.value)}
                  className="h-8 text-sm"
                  placeholder="Optional"
                />
              </div>
            </CardContent>
          </Card>

          {selectedObjData && (
            <Card className="mt-4">
              <CardHeader className="py-3">
                <CardTitle className="text-sm">Object Properties</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {selectedObjData.type === 'single_row' && (
                  <>
                    <div>
                      <Label className="text-xs">Row Label</Label>
                      <Input 
                        value={selectedObjData.properties.row_label || ''}
                        onChange={(e) => updateObjectProperty('row_label', e.target.value)}
                        className="h-8 text-sm"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Seat Count</Label>
                      <Input 
                        type="number"
                        value={selectedObjData.properties.seat_count || 10}
                        onChange={(e) => updateObjectProperty('seat_count', parseInt(e.target.value) || 1)}
                        className="h-8 text-sm"
                        min={1}
                        max={50}
                      />
                    </div>
                  </>
                )}

                {selectedObjData.type === 'round_table' && (
                  <>
                    <div>
                      <Label className="text-xs">Table Label</Label>
                      <Input 
                        value={selectedObjData.properties.label || ''}
                        onChange={(e) => updateObjectProperty('label', e.target.value)}
                        className="h-8 text-sm"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Seats</Label>
                      <Input 
                        type="number"
                        value={selectedObjData.properties.seats || 8}
                        onChange={(e) => updateObjectProperty('seats', parseInt(e.target.value) || 4)}
                        className="h-8 text-sm"
                        min={4}
                        max={12}
                      />
                    </div>
                  </>
                )}

                {selectedObjData.type === 'ga_zone' && (
                  <>
                    <div>
                      <Label className="text-xs">Zone Label</Label>
                      <Input 
                        value={selectedObjData.properties.label || ''}
                        onChange={(e) => updateObjectProperty('label', e.target.value)}
                        className="h-8 text-sm"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Capacity</Label>
                      <Input 
                        type="number"
                        value={selectedObjData.properties.capacity || 100}
                        onChange={(e) => updateObjectProperty('capacity', parseInt(e.target.value) || 10)}
                        className="h-8 text-sm"
                        min={10}
                        max={1000}
                      />
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          )}

          {/* Object count */}
          <div className="mt-4 text-xs text-gray-500">
            {canvasObjects.length} object(s) on canvas
          </div>
        </div>
      </div>

      {/* Powered by footer */}
      {branding?.layout?.show_powered_by !== false && (
        <div className="text-center py-2 text-xs text-gray-400 border-t">
          Powered by <a href={getBaseUrl()} target="_blank" rel="noopener noreferrer" className="text-indigo-500">{BRAND_NAME}</a>
        </div>
      )}
    </div>
  );
}

// Render individual canvas object
function renderObject(obj, zoom, colors, isSelected) {
  const seatAvailable = colors.seat_available || '#10b981';
  const baseSize = 30 * zoom;

  switch (obj.type) {
    case 'single_row':
      const seatCount = obj.properties?.seat_count || 10;
      const spacing = (obj.properties?.spacing || 35) * zoom;
      return (
        <div className="flex items-center">
          {Array.from({ length: seatCount }).map((_, i) => (
            <div
              key={i}
              className="rounded flex items-center justify-center text-white text-xs font-medium"
              style={{
                width: baseSize,
                height: baseSize,
                backgroundColor: seatAvailable,
                marginRight: i < seatCount - 1 ? spacing - baseSize : 0
              }}
            >
              {obj.properties?.row_label || 'A'}{i + 1}
            </div>
          ))}
        </div>
      );

    case 'round_table':
      const tableSeats = obj.properties?.seats || 8;
      const radius = (obj.properties?.radius || 60) * zoom;
      return (
        <div 
          className="relative rounded-full bg-amber-100 border-2 border-amber-300"
          style={{ width: radius * 2, height: radius * 2 }}
        >
          <div className="absolute inset-0 flex items-center justify-center text-xs font-medium text-amber-700">
            {obj.properties?.label || 'T1'}
          </div>
          {Array.from({ length: tableSeats }).map((_, i) => {
            const angle = (i * 360) / tableSeats - 90;
            const rad = (angle * Math.PI) / 180;
            const x = radius + Math.cos(rad) * (radius - 15 * zoom);
            const y = radius + Math.sin(rad) * (radius - 15 * zoom);
            return (
              <div
                key={i}
                className="absolute rounded-full"
                style={{
                  width: 20 * zoom,
                  height: 20 * zoom,
                  left: x - 10 * zoom,
                  top: y - 10 * zoom,
                  backgroundColor: seatAvailable
                }}
              />
            );
          })}
        </div>
      );

    case 'seat':
      return (
        <div
          className="rounded flex items-center justify-center text-white text-xs font-medium"
          style={{
            width: baseSize,
            height: baseSize,
            backgroundColor: seatAvailable
          }}
        >
          {obj.properties?.label || 'S'}
        </div>
      );

    case 'ga_zone':
      return (
        <div
          className="rounded-lg border-2 border-dashed flex items-center justify-center"
          style={{
            width: (obj.properties?.width || 200) * zoom,
            height: (obj.properties?.height || 150) * zoom,
            backgroundColor: 'rgba(99, 102, 241, 0.1)',
            borderColor: colors.primary || '#6366f1'
          }}
        >
          <div className="text-center">
            <div className="font-medium" style={{ color: colors.primary || '#6366f1' }}>
              {obj.properties?.label || 'GA'}
            </div>
            <div className="text-xs text-gray-500">
              Cap: {obj.properties?.capacity || 100}
            </div>
          </div>
        </div>
      );

    default:
      return (
        <div 
          className="w-20 h-20 bg-gray-300 rounded flex items-center justify-center"
          style={{ transform: `scale(${zoom})` }}
        >
          {obj.type}
        </div>
      );
  }
}
